import { Button, I18nProvider, usePageTitle, useTranslate } from '@athena/ui'
import { useRouterState, type ErrorComponentProps } from '@tanstack/react-router'
import { useEffect } from 'react'

import { DEFAULT_LANGUAGE, languageFromPathname } from '../lib/language.js'

/**
 * A screen that failed to render, in the language of its URL.
 *
 * The router's own error screen is English and offers to print the error's
 * message. An administrator is not the audience for that either: the words
 * that help are what did not happen and what to do, and the error itself goes
 * to the console. Its own provider, because the layout that would have
 * supplied one may be what failed.
 */
export function AdminRouteError({ error }: ErrorComponentProps) {
  const pathname = useRouterState({ select: (state) => state.location.pathname })
  const language = languageFromPathname(pathname) ?? DEFAULT_LANGUAGE

  useEffect(() => {
    console.error(error)
  }, [error])

  return (
    <I18nProvider language={language}>
      <AdminRouteErrorBody />
    </I18nProvider>
  )
}

function AdminRouteErrorBody() {
  const t = useTranslate()
  usePageTitle(t('appError.crashTitle'))

  return (
    <main className="flex flex-col items-start gap-4 p-8">
      <h1 tabIndex={-1} className="text-h2 font-bold text-text outline-none">
        {t('appError.crashTitle')}
      </h1>
      <p className="text-body text-text-secondary">{t('appError.crashBody')}</p>
      <Button variant="secondary" onClick={() => globalThis.location.reload()}>
        {t('appError.reload')}
      </Button>
    </main>
  )
}
